import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  Refresh as RetryIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  ErrorOutline as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentTemplateService } from '../services/documentTemplateService';
import { IDocumentTemplate } from '../types/document';
import { stitchColors, stitchFonts } from '../theme/tokens';

export const DocumentTemplates: React.FC = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [customTitle, setCustomTitle] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');

  // Source PDF Preview modal
  const [previewTemplate, setPreviewTemplate] = useState<IDocumentTemplate | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const activePreviewRequestIdRef = useRef<number>(0);
  const activePreviewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (activePreviewUrlRef.current) {
        URL.revokeObjectURL(activePreviewUrlRef.current);
        activePreviewUrlRef.current = null;
      }
    };
  }, []);

  // Extracted text preview modal
  const [textPreviewTemplate, setTextPreviewTemplate] = useState<IDocumentTemplate | null>(null);

  // Fetch all uploaded document templates
  const {
    data: templates = [],
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['uploadedDocumentTemplates'],
    queryFn: documentTemplateService.getUploadedTemplates,
    refetchInterval: (query) => {
      const data = query.state.data as IDocumentTemplate[] | undefined;
      const isProcessing = data?.some(
        (t) =>
          t.sourceImport?.status === 'queued' ||
          t.sourceImport?.status === 'extracting'
      );
      return isProcessing ? 3000 : false;
    },
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ file, title }: { file: File; title?: string }) => {
      return await documentTemplateService.uploadTemplatePdf(file, title);
    },
    onSuccess: () => {
      setSelectedFile(null);
      setCustomTitle('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadSuccess('Template uploaded successfully. OCR extraction is in progress.');
      setUploadError('');
      queryClient.invalidateQueries({ queryKey: ['uploadedDocumentTemplates'] });
    },
    onError: (err: any) => {
      setUploadError(err.response?.data?.error || err.message || 'Failed to upload template PDF');
      setUploadSuccess('');
    },
  });

  // Retry mutation
  const retryMutation = useMutation({
    mutationFn: async (importId: string) => {
      return await documentTemplateService.retryImport(importId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uploadedDocumentTemplates'] });
    },
    onError: (err: any) => {
      setUploadError(err.response?.data?.error || err.message || 'Failed to retry extraction');
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setUploadError('Only PDF files are supported.');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setUploadError('');
      setUploadSuccess('');
      if (!customTitle) {
        const defaultTitle = file.name.replace(/\.pdf$/i, '').replace(/[-_]/g, ' ');
        setCustomTitle(defaultTitle);
      }
    }
  };

  const handleUploadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      setUploadError('Please select a PDF file to upload.');
      return;
    }
    uploadMutation.mutate({ file: selectedFile, title: customTitle.trim() || undefined });
  };

  const handleOpenPdfPreview = async (template: IDocumentTemplate) => {
    if (!template._id) return;
    const requestId = ++activePreviewRequestIdRef.current;

    if (activePreviewUrlRef.current) {
      URL.revokeObjectURL(activePreviewUrlRef.current);
      activePreviewUrlRef.current = null;
    }
    setPreviewPdfUrl(null);
    setPreviewTemplate(template);
    setLoadingPreview(true);
    setPreviewError('');
    try {
      const blob = await documentTemplateService.getSourcePdfBlob(template._id);
      if (activePreviewRequestIdRef.current !== requestId) {
        return;
      }
      const url = URL.createObjectURL(blob);
      activePreviewUrlRef.current = url;
      setPreviewPdfUrl(url);
    } catch {
      if (activePreviewRequestIdRef.current === requestId) {
        setPreviewError('Failed to load source PDF preview.');
      }
    } finally {
      if (activePreviewRequestIdRef.current === requestId) {
        setLoadingPreview(false);
      }
    }
  };

  const handleClosePdfPreview = () => {
    activePreviewRequestIdRef.current++;
    if (activePreviewUrlRef.current) {
      URL.revokeObjectURL(activePreviewUrlRef.current);
      activePreviewUrlRef.current = null;
    }
    setPreviewPdfUrl(null);
    setPreviewTemplate(null);
    setPreviewError('');
    setLoadingPreview(false);
  };

  const renderStatusChip = (template: IDocumentTemplate) => {
    const status = template.sourceImport?.status;
    if (status === 'extracting') {
      return (
        <Chip
          icon={<CircularProgress size={14} color="inherit" />}
          label="Extracting..."
          color="warning"
          size="small"
          variant="outlined"
        />
      );
    }
    if (status === 'queued') {
      return (
        <Chip
          icon={<PendingIcon fontSize="small" />}
          label="Queued"
          color="info"
          size="small"
          variant="outlined"
        />
      );
    }
    if (status === 'failed') {
      return (
        <Chip
          icon={<ErrorIcon fontSize="small" />}
          label="Failed"
          color="error"
          size="small"
          variant="outlined"
        />
      );
    }
    return (
      <Chip
        icon={<SuccessIcon fontSize="small" />}
        label="Ready"
        color="success"
        size="small"
        variant="outlined"
      />
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 4 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontFamily: stitchFonts.headline, fontWeight: 700, mb: 1 }}
        >
          Document Templates
        </Typography>
        <Typography variant="body1" sx={{ color: stitchColors.onSurfaceVariant }}>
          Upload and manage reusable PDF document templates. All lawyers can preview and use templates uploaded here when creating or revising client documents.
        </Typography>
      </Box>

      {/* Upload Section */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          border: `1px dashed ${stitchColors.outlineVariant}`,
          backgroundColor: stitchColors.surfaceContainerLowest,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Upload New Document Template
          </Typography>

          {uploadSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess('')}>
              {uploadSuccess}
            </Alert>
          )}

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>
              {uploadError}
            </Alert>
          )}

          <form onSubmit={handleUploadSubmit}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Template Title (Optional)"
                  placeholder="e.g. Standard Last Will & Testament"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  disabled={uploadMutation.isPending}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  style={{ display: 'none' }}
                  id="template-pdf-input"
                  onChange={handleFileChange}
                  disabled={uploadMutation.isPending}
                />
                <label htmlFor="template-pdf-input">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<UploadIcon />}
                    disabled={uploadMutation.isPending}
                    sx={{ textTransform: 'none' }}
                  >
                    {selectedFile ? selectedFile.name : 'Select PDF File'}
                  </Button>
                </label>
              </Grid>

              <Grid item xs={12} sm={2}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={!selectedFile || uploadMutation.isPending}
                  startIcon={uploadMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Template Library Table */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          borderColor: stitchColors.outlineVariant,
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Shared Template Library ({templates.length})
          </Typography>
          <Button
            size="small"
            startIcon={<RetryIcon />}
            onClick={() => refetch()}
            disabled={isLoading}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>
        <Divider />

        {queryError && (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load templates: {(queryError as any)?.message || 'Unknown error'}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <DocumentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="subtitle1" color="text.secondary">
              No uploaded document templates yet.
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Upload a PDF template above to start using it across client documents.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: stitchColors.surfaceContainerLow }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Original File</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Uploaded By</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((tpl) => (
                  <TableRow key={tpl._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tpl.title}
                      </Typography>
                      {tpl.placeholderMappings && tpl.placeholderMappings.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {tpl.placeholderMappings.length} placeholder fields detected
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {tpl.sourceImport?.originalFilename || 'template.pdf'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {tpl.uploader?.name || tpl.userEmail}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tpl.uploader?.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderStatusChip(tpl)}</TableCell>
                    <TableCell>v{tpl.version || 1}</TableCell>
                    <TableCell>
                      {tpl.createdAt ? new Date(tpl.createdAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Tooltip title="Preview Original PDF">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Preview Original PDF"
                              onClick={() => handleOpenPdfPreview(tpl)}
                              disabled={tpl.sourceImport?.status === 'extracting'}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {tpl.extractedText && (
                          <Tooltip title="View Extracted Text">
                            <IconButton
                              size="small"
                              aria-label="View Extracted Text"
                              onClick={() => setTextPreviewTemplate(tpl)}
                            >
                              <DocumentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {tpl.canRetry && tpl.sourceImport?.status === 'failed' && (
                          <Tooltip title="Retry Extraction">
                            <Button
                              size="small"
                              color="warning"
                              variant="outlined"
                              startIcon={retryMutation.isPending ? <CircularProgress size={14} /> : <RetryIcon />}
                              onClick={() => tpl.sourceImport?._id && retryMutation.mutate(tpl.sourceImport._id)}
                              disabled={retryMutation.isPending}
                              sx={{ textTransform: 'none' }}
                            >
                              Retry
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* PDF Source Preview Dialog */}
      <Dialog
        open={Boolean(previewTemplate)}
        onClose={handleClosePdfPreview}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '80vh', borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            {previewTemplate?.title || 'PDF Preview'}
          </Typography>
          <IconButton onClick={handleClosePdfPreview} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {loadingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : previewError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{previewError}</Alert>
            </Box>
          ) : previewPdfUrl ? (
            <iframe
              src={previewPdfUrl}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: 'none', flex: 1 }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePdfPreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Extracted Text Preview Dialog */}
      <Dialog
        open={Boolean(textPreviewTemplate)}
        onClose={() => setTextPreviewTemplate(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Extracted Content: {textPreviewTemplate?.title}
          </Typography>
          <IconButton onClick={() => setTextPreviewTemplate(null)} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {textPreviewTemplate?.placeholderMappings && textPreviewTemplate.placeholderMappings.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Detected Placeholders:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {textPreviewTemplate.placeholderMappings.map((ph) => (
                  <Chip
                    key={ph.id}
                    label={`[${ph.label}]`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Box>
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Plain Text Representation:
          </Typography>
          <Box
            component="pre"
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 400,
              overflowY: 'auto',
              fontSize: '0.875rem',
              fontFamily: 'monospace',
            }}
          >
            {textPreviewTemplate?.extractedText || 'No extracted text available.'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTextPreviewTemplate(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentTemplates;
